import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import {
	Box,
	FormControl,
	MenuItem,
	Select,
	InputLabel,
	Snackbar,
	CircularProgress,
	Typography,
	List,
	ListItem,
	ListItemText,
	Divider
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { BaseLayout, Lists, Tag } from '../..';
import { getCampaigns, getCampaignDetails } from '../../../api/campaigns';
import Alert from '@mui/material/Alert';

const useStyles = makeStyles((theme) => ({
	paper: {
		padding: theme.spacing(2),
		marginBottom: theme.spacing(3),
		display: 'flex',
		overflow: 'auto',
		alignItems: 'center',
		justifyContent: 'space-between'
	},
	detailsPaper: {
		padding: theme.spacing(2),
		marginBottom: theme.spacing(3)
	},
	phaseTagsContainer: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: theme.spacing(2),
		marginBottom: theme.spacing(2)
	},
	select: { minWidth: 250, width: '100%' },
	wrapper: {
		margin: theme.spacing(1),
		position: 'relative',
		width: '30%'
	},
	selectProgress: {
		position: 'absolute',
		top: '50%',
		left: '50%',
		marginTop: -12,
		marginLeft: -12
	},
	placeholderText: {
		color: theme.palette.text.secondary,
		textAlign: 'center',
		padding: theme.spacing(4)
	},
	historyContainer: {
		marginTop: theme.spacing(3)
	}
}));

export default function Campaigns() {
	const classes = useStyles();

	// Loading states
	const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(true);
	const [isLoadingDetails, setIsLoadingDetails] = useState(false);

	// Data states
	const [campaigns, setCampaigns] = useState([]);
	const [campaignDetails, setCampaignDetails] = useState(null);

	// Selection states
	const [selectedCampaignId, setSelectedCampaignId] = useState('');
	const [selectedPhaseId, setSelectedPhaseId] = useState(null);

	// Notification state
	const [notification, setNotification] = useState({
		isOpen: false,
		message: '',
		severity: 'success'
	});

	// Fetch campaigns and templates on component mount
	useEffect(() => {
		setIsLoadingCampaigns(true);
		getCampaigns()
			.then((campaignsRes) => {
				const campaignsData = campaignsRes.data;
				if (campaignsData.success) {
					setCampaigns(campaignsData.campaigns);
					if (campaignsData.campaigns.length === 1) {
						setSelectedCampaignId(campaignsData.campaigns[0].Id);
					}
				} else {
					setNotification({
						isOpen: true,
						message: campaignsData.message || 'Error fetching campaigns.',
						severity: 'error'
					});
				}
			})
			.catch(() =>
				setNotification({
					isOpen: true,
					message: 'Error fetching initial data.',
					severity: 'error'
				})
			)
			.finally(() => setIsLoadingCampaigns(false));
	}, []);

	// Fetch campaign details when a campaign is selected
	useEffect(() => {
		if (!selectedCampaignId) {
			setCampaignDetails(null);
			return;
		}

		setIsLoadingDetails(true);
		setCampaignDetails(null); // Clear previous details
		setSelectedPhaseId(null); // Reset phase selection

		getCampaignDetails(selectedCampaignId)
			.then((res) => {
				const detailsData = res.data;
				if (detailsData.success) {
					setCampaignDetails(detailsData.campaign);
					const firstPhaseWithListings = detailsData.campaign.phases.find(
						(p) => p.listings.length > 0
					);
					setSelectedPhaseId(firstPhaseWithListings ? firstPhaseWithListings.Id : null);
				} else {
					setNotification({
						isOpen: true,
						message: detailsData.message || 'Error fetching campaign details.',
						severity: 'error'
					});
				}
			})
			.catch(() =>
				setNotification({
					isOpen: true,
					message: 'Error fetching campaign details.',
					severity: 'error'
				})
			)
			.finally(() => setIsLoadingDetails(false));
	}, [selectedCampaignId]);

	const handleCampaignChange = (event) => {
		setSelectedCampaignId(event.target.value);
	};

	const handlePhaseSelect = (phaseId) => {
		setSelectedPhaseId(phaseId);
	};

	const handleCloseNotification = (event, reason) => {
		if (reason === 'clickaway') {
			return;
		}
		setNotification({ ...notification, isOpen: false });
	};

	const selectedPhase = campaignDetails?.phases.find((p) => p.Id === selectedPhaseId) || null;

	const listingsForSelectedPhase = selectedPhase?.listings || [];

	const titles = [
		{ label: 'Image', numeric: false, id: 'Pics' },
		{ label: 'Gem Score', numeric: true, id: 'Knockability' },
		{ label: 'Address', numeric: false, id: 'Address' },
		{ label: 'Area', numeric: false, id: 'Area' },
		{ label: 'SubArea', numeric: false, id: 'SA' },
		{ label: 'Price', numeric: true, id: 'Price' },
		{ label: 'Assessment', numeric: true, id: 'assessment' },
		{ label: 'Total Bedrooms', numeric: true, id: 'TotBR' },
		{ label: 'Total Bathrooms', numeric: true, id: 'TotBaths' },
		{ label: 'Kitchens', numeric: true, id: 'NumKitchens' },
		{ label: 'Total Floor Area', numeric: true, id: 'TotFlArea' },
		{ label: 'Year Built', numeric: true, id: 'YrBlt' },
		{ label: 'Frontage (Feet)', numeric: true, id: 'FrontageFeet' },
		{ label: 'Lot Size', numeric: true, id: 'LotSzSqFt' },
		{ label: 'Assigned At', numeric: false, id: 'AssignedAt' }
	];

	const cells = [
		'Knockability',
		'Address',
		'Area',
		'SA',
		'Price',
		'assessment',
		'TotBR',
		'TotBaths',
		'NumKitchens',
		'TotFlArea',
		'YrBlt',
		'FrontageFeet',
		'LotSzSqFt',
		'AssignedAt'
	];

	return (
		<BaseLayout>
			<Snackbar
				anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
				open={notification.isOpen}
				autoHideDuration={6000}
				onClose={handleCloseNotification}
			>
				<Alert
					onClose={handleCloseNotification}
					severity={notification.severity}
					variant="filled"
				>
					{notification.message}
				</Alert>
			</Snackbar>

			<Grid container spacing={3}>
				<Grid item xs={12}>
					<Paper className={classes.paper}>
						<Typography variant="h6">Campaigns</Typography>
						<Box className={classes.wrapper}>
							<FormControl variant="outlined" className={classes.select} fullWidth>
								<InputLabel htmlFor="campaign-dropdown">Select Campaign</InputLabel>
								<Select
									onChange={handleCampaignChange}
									label="Select Campaign"
									value={selectedCampaignId}
									disabled={isLoadingCampaigns || campaigns.length === 0}
								>
									{campaigns.map((campaign) => (
										<MenuItem key={campaign.Id} value={campaign.Id}>
											{campaign.Name}
										</MenuItem>
									))}
								</Select>
								{isLoadingCampaigns && (
									<CircularProgress
										size={24}
										className={classes.selectProgress}
									/>
								)}
							</FormControl>
						</Box>
					</Paper>

					{isLoadingDetails ? (
						<Grid item xs={12} style={{ textAlign: 'center', padding: '40px' }}>
							<CircularProgress />
						</Grid>
					) : campaignDetails ? (
						<Grid item xs={12}>
							<Paper className={classes.detailsPaper}>
								<Box className={classes.phaseTagsContainer}>
									{campaignDetails.phases.map((phase) => (
										<Tag
											key={phase.Id}
											tag={phase.PhaseName}
											count={phase.listings.length}
											handleClick={() => handlePhaseSelect(phase.Id)}
											isSelected={phase.Id === selectedPhaseId}
										/>
									))}
								</Box>
								{selectedPhase && (
									<Box>
										<Typography variant="subtitle1" gutterBottom>
											<strong>Using Template:</strong>{' '}
											{selectedPhase.LetterTemplateName || 'N/A'}
										</Typography>

										<Box className={classes.historyContainer}>
											<Typography variant="h6" gutterBottom>
												Batch Send History
											</Typography>
											{selectedPhase.sendHistory.length > 0 ? (
												<List dense>
													{selectedPhase.sendHistory.map((log, index) => (
														<React.Fragment key={index}>
															<ListItem>
																<ListItemText
																	primary={`Sent to ${log.count} properties`}
																	secondary={new Date(
																		log.SentAt
																	).toLocaleString()}
																/>
															</ListItem>
															{index <
																selectedPhase.sendHistory.length -
																	1 && <Divider />}
														</React.Fragment>
													))}
												</List>
											) : (
												<Typography color="textSecondary">
													No batches have been sent for this phase yet.
												</Typography>
											)}
										</Box>
									</Box>
								)}
							</Paper>
							{listingsForSelectedPhase.length > 0 ? (
								<Lists
									isSearch={true}
									isCollection={false}
									listHeads={titles}
									listCells={cells}
									listings={listingsForSelectedPhase}
									initialOrderBy={'AssignedAt'}
									isTaskBoard={false}
									totalCount={listingsForSelectedPhase.length}
									disableFetching={true}
								/>
							) : (
								<Paper className={classes.placeholderText}>
									<Typography>
										{selectedPhase
											? 'No properties are currently in this phase.'
											: 'Select a phase to view properties.'}
									</Typography>
								</Paper>
							)}
						</Grid>
					) : (
						<Grid item xs={12}>
							<Paper className={classes.placeholderText}>
								<Typography>
									{campaigns.length > 0
										? 'Please select a campaign to view its details.'
										: 'No campaigns available.'}
								</Typography>
							</Paper>
						</Grid>
					)}
				</Grid>
			</Grid>
		</BaseLayout>
	);
}
